import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { createIntl, IntlProvider } from 'react-intl'

import type { Translation } from '@subaio/api-types/shared'
import {
	MediaQueryProvider,
	NoOpErrorBoundary,
	RouterProvider,
	renderDefaultSnackbar,
	SnackbarProvider,
	ThemeProvider,
	useAsyncError,
	useStore,
} from '@subaio/components'
import { type Api, logError, type Page, type Router, request } from '@subaio/utils'
import { defaultBreakpointQueries } from '@subaio/webapp'
import { ContainerStatus, initializeContainer, invalidate } from '@subaio/webapp/src/containers'

import type { Container } from 'lib/container'
import { getContainer } from 'lib/getContainer'
import { intlFormats } from 'lib/intlFormats'
import type { Store } from 'lib/store'
import type { getUserAuth } from 'lib/userAuth'

import { GlobalStyle, getTheme } from 'styles'

import { Error } from 'components/Error'
import { ModalProvider } from 'components/Modal'
import { Page as PageComponent } from 'components/Page'

type Props = {
	api: Api
	store: Store
	router: Router
	containers: Record<string, Container>

	setUser: (user: Parameters<typeof getUserAuth>[0]) => void
}

export const App = ({ api, store, router, containers, setUser }: Props) => {
	const { page, status, data, translation, userAuth } = useStore(store)

	const container = getContainer(containers, page, userAuth)
	const [pageTitle, setPageTitle] = useState<ReactNode>(undefined)
	const locale = translation?.locale ?? 'da-DK'

	const setLocale = useCallback(
		async (locale: string) => {
			const translation = await request<Translation>({ url: `/locales/${locale}` })

			store.set({ translation })
		},
		[store],
	)

	const setData = useCallback(
		(update: Record<string, any>) => {
			const { data, page: currentPage } = store.getState()
			// Abort state updates if page has changed
			if (page !== currentPage) return
			store.set({ data: { ...data, ...update } })
		},
		[store, page],
	)

	const updatePage = useCallback(
		(page: Page) => {
			store.set({ page, status: ContainerStatus.Initializing, data: {} })
		},
		[store],
	)

	const invalidateSelf = useCallback(
		(...invalidDependencies: Array<string>) =>
			invalidate(
				store,
				containers,
				{ query: page.query, updatePage, router, setUser, api, locale, userAuth, setPageTitle, setLocale },
				async () => undefined,
				invalidDependencies,
			),
		[store, containers, page.query, updatePage, router, setUser, api, locale, userAuth, setLocale],
	)

	const title = useMemo(() => {
		if (!translation) return undefined

		const { formatMessage } = createIntl({
			locale: translation.locale,
			messages: translation.messages,
			formats: intlFormats,
			defaultFormats: intlFormats,
		})

		return formatMessage({ id: 'title' })
	}, [translation])

	const theme = useMemo(() => getTheme(), [])

	useEffect(() => {
		if (typeof title !== 'string') return

		const original = document.title
		document.title = original ? `${title} • ${original}` : title

		return () => {
			document.title = original
		}
	}, [title])

	useEffect(() => {
		if (!translation) return

		document.documentElement.lang = translation.locale
	}, [translation])

	const handleError = useAsyncError()
	useEffect(() => {
		;(async () => {
			if (status !== ContainerStatus.Initializing) return

			switch (container.authentication.type) {
				case 'public': {
					await initializeContainer(
						store,
						container,
						{ query: page.query, updatePage, router, setUser, api, locale, userAuth, setPageTitle, setLocale },
						async (path) => api.get(path),
					)
					break
				}
				case 'private': {
					await initializeContainer(
						store,
						container,
						{ query: page.query, updatePage, router, setUser, api, locale, userAuth, setPageTitle, setLocale },
						async (path) => api.get(path),
					)
					break
				}
			}
			if (!store.getState().translation) {
				await setLocale('en-DK')
			}
		})().catch(handleError)
	}, [store, container, status, updatePage, page, router, setUser, api, locale, userAuth, setLocale, handleError])

	const props = {
		...page.params,
		api,
		locale,
		userAuth,
		query: page.query,
		invalidateSelf,
		setData,
		setUser,
		setPageTitle,
		setLocale,
	}

	return (
		<ThemeProvider theme={theme}>
			<GlobalStyle />
			<IntlProvider
				locale={locale}
				defaultLocale='en-DK'
				formats={intlFormats}
				defaultFormats={intlFormats}
				messages={translation?.messages ?? {}}
			>
				<RouterProvider router={router} page={page} onPageChange={updatePage}>
					<MediaQueryProvider queries={defaultBreakpointQueries}>
						<ModalProvider mode='fixed'>
							<PageComponent
								brandId={userAuth.status === 'authenticated' ? userAuth.user.brandId : null}
								title={pageTitle}
							>
								<SnackbarProvider render={renderDefaultSnackbar}>
									<NoOpErrorBoundary
										onError={(error, info) => {
											logError(error, info)

											store.set({ status: ContainerStatus.Errored })
										}}
									>
										<Fragment key={page.name}>
											{status === ContainerStatus.Running && <container.Running {...data} {...props} />}
											{status === ContainerStatus.Loading && <container.Loading {...data} {...props} />}
											{status === ContainerStatus.Initializing && (
												<container.Loading {...container.defaults} {...props} />
											)}
											{status === ContainerStatus.Errored && <Error />}
										</Fragment>
									</NoOpErrorBoundary>
								</SnackbarProvider>
							</PageComponent>
						</ModalProvider>
					</MediaQueryProvider>
				</RouterProvider>
			</IntlProvider>
		</ThemeProvider>
	)
}
